import { useRef, useState } from "react";
import { toast } from "sonner";
import { Copy, ImagePlus, Trash2, BookOpen, Share2 } from "lucide-react";
import { shareLink } from "@/components/quotes/ShareQuoteDialog";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg, API } from "@/lib/api";
import { FanWorkFields, RightsHoldNotice } from "@/components/fanworks/FanWorkFields";

const SHARING = [["off", "Off (team only)"], ["link", "Direct link only"], ["listed", "Listed in More Universes"]];

export const coverUrl = (s) => `${API}/stories/${s.id}/cover?v=${s.cover_v || ""}`;

export const BookDetails = ({ story, onSaved }) => {
  const [author, setAuthor] = useState(story.author || "");
  const [subtitle, setSubtitle] = useState(story.subtitle || "");
  const [busy, setBusy] = useState(false);
  const file = useRef(null);
  const meta = async (patch, msg) => { try { await api.patch(`/stories/${story.id}/meta`, patch); if (msg) toast.success(msg); onSaved(); } catch (e) { toast.error(errMsg(e)); } };
  const upload = async (f) => {
    if (!f) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", f);
    try { await api.post(`/stories/${story.id}/cover`, fd); toast.success("Cover uploaded"); onSaved(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const link = `${window.location.origin}/read/${story.id}`;
  return (
    <section className="grid grid-cols-1 gap-6 rounded-md border border-[#2D3442] bg-[#0F131A] p-5 lg:grid-cols-[160px_1fr_1fr]" data-testid="book-details">
      <div>
        <div className="flex aspect-[2/3] w-40 items-center justify-center overflow-hidden rounded border border-dashed border-[#2D3442] bg-[#13171F]">
          {story.cover_path ? <img src={coverUrl(story)} alt="Cover" className="h-full w-full object-cover" data-testid="cover-preview" /> : <span className="px-3 text-center text-xs text-[#A8A090]">No cover yet</span>}
        </div>
        <input ref={file} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="cover-file-input" />
        <div className="mt-2 flex gap-1">
          <Button size="sm" variant="outline" disabled={busy} onClick={() => file.current?.click()} data-testid="cover-upload-button"><ImagePlus className="mr-1 h-3.5 w-3.5" />{story.cover_path ? "Replace" : "Upload"}</Button>
          {story.cover_path && <Button size="sm" variant="ghost" onClick={() => api.delete(`/stories/${story.id}/cover`).then(onSaved)} data-testid="cover-remove-button"><Trash2 className="h-3.5 w-3.5" /></Button>}
        </div>
      </div>
      <div className="space-y-3">
        <div className="lf-label">Title page</div>
        <Input value={author} onChange={(e) => setAuthor(e.target.value)} placeholder="Author name" data-testid="book-author-input" />
        <Input value={subtitle} onChange={(e) => setSubtitle(e.target.value)} placeholder="Subtitle, e.g. Book One of the Ember Reaches" data-testid="book-subtitle-input" />
        <Button size="sm" onClick={() => meta({ author, subtitle }, "Title page saved")} data-testid="book-details-save">Save title page</Button>
      </div>
      <div className="space-y-3">
        <div className="lf-label">Reader sharing</div>
        {story.rights_hold && <RightsHoldNotice />}
        <Select disabled={!!story.rights_hold} value={story.sharing || "off"} onValueChange={(v) => meta({ sharing: v }, v === "off" ? "Sharing off" : v === "link" ? "Anyone signed in with the link can read" : "Listed in More Universes")}>
          <SelectTrigger data-testid="sharing-select"><SelectValue /></SelectTrigger>
          <SelectContent>{SHARING.map(([k, l]) => <SelectItem key={k} value={k} data-testid={`sharing-${k}`}>{l}</SelectItem>)}</SelectContent>
        </Select>
        {story.sharing && story.sharing !== "off" && (
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="outline" onClick={() => navigator.clipboard.writeText(link).then(() => toast.success("Reader link copied"))} data-testid="copy-reader-link"><Copy className="mr-1 h-3.5 w-3.5" />Copy reader link</Button>
            <Button size="sm" variant="outline" onClick={() => navigator.clipboard.writeText(shareLink(story.id)).then(() => toast.success("Share link copied. It shows a cover preview on X, Facebook and Discord."))} data-testid="copy-share-link"><Share2 className="mr-1 h-3.5 w-3.5" />Copy share link</Button>
          </div>
        )}
        <label className="flex items-center gap-2 text-sm text-[#F3EBDD]"><Switch checked={story.feedback_enabled !== false} onCheckedChange={(v) => meta({ feedback_enabled: v })} data-testid="feedback-toggle" />Reader comments & ratings</label>
        <FanWorkFields story={story} meta={meta} />
        <Button size="sm" variant="ghost" asChild><Link to={`/read/${story.id}`} data-testid="open-reader-link"><BookOpen className="mr-1 h-3.5 w-3.5" />Open reader</Link></Button>
      </div>
    </section>
  );
};
